/* eslint-disable no-param-reassign */
import {
  StateCreator, create,
} from 'zustand';
import type { FreeLayoutPluginContext, WorkflowContentChangeEvent } from '@flowgram.ai/free-layout-editor';
import { WorkflowContentChangeType } from '@flowgram.ai/free-layout-editor';
import { immer } from 'zustand/middleware/immer';
import type { McpType, McpCategoriesType } from '$/domains/mcp/type';
import type { FeedbackCallbackType } from '$/pages/RoboneoChatBoard/type';
import { getPublishDisableStatus } from '$/pages/RoboneoFlow/utils/publishCommunityUtil';
import type { NodeMaterialItem } from '$/domains/material/type';
import { mtstatTrack } from '$/utils/mtstat';

interface MCPTaskingItem {
  estimated_total_ms?: number;
}

interface INodeDisabledConfig {
  // 有禁用的选项即生效
  disabled: boolean;
  disabledByForm?: boolean;
  disabledByPort?: boolean;
}

export interface IExecuteWorkflowItem {
  // 批量执行的同一个工作流中nodeId列表
  nodeIds: string[];
  // 是否通知过节点参数配置错误
  isNotice: boolean;
}
export interface TFlowState {
  mcpConfigList: McpType[];
  mcpCategoriesList: McpCategoriesType[];
  mcpNameMap: Record<string, string>;
  material_list: NodeMaterialItem[];
  nodeDisabledConfig: Record<string, INodeDisabledConfig>;
  mcpTaskingMap: Record<string, MCPTaskingItem>;
  hasNodeOperation: boolean; // 用户是否操作过节点
  roomId: string;
  hoverNodeId: string;
  isHovered: boolean;
  uploadingNodes: string[];
  executeWorkflowOptions: Array<IExecuteWorkflowItem>;
  inShared: boolean;
  flowNmae: string;
  allExecuteIdMap: Record<string, boolean>;
  hasAllNodes: boolean;
  disabledPublish: boolean;
  disabledPublishMessage: string;
  cropingVideNodeIds: string[];
  flowInitialized: boolean; // 画布是否初始化完成
  isExecutingSSE: boolean;
  nodeHistiryInfoId: string;
  costCalcResult: {
    cost: number;
    time: number;
  };
  costCalcModalOpen: boolean;
  costCalcExecuteCallback: (() => void) | null;
  costCalcWillExecuteNodeIds: string[];
  editingNodeId: string;
  feedbackCallback?: FeedbackCallbackType;
}

const flowSlice: StateCreator<TFlowState, [
  ['zustand/immer', never],
]> = () => ({
  // mcp配置数据
  mcpConfigList: [],

  // mcp分类集合
  mcpCategoriesList: [],

  // id 映射 名称 name
  mcpNameMap: {},

  // 分享时素材列表
  material_list: [],

  // 节点禁用状态
  nodeDisabledConfig: {},
  // 任务中集合
  mcpTaskingMap: {},
  // 全部执行id集合
  allExecuteIdMap: {},
  // 用户是否操作过节点
  hasNodeOperation: false,

  // hover节点时的id
  hoverNodeId: '',
  isHovered: false,

  roomId: '',

  uploadingNodes: [],

  executeWorkflowOptions: [],
  // 是否在分享页
  inShared: false,

  // 是否在执行 SSE 中
  isExecutingSSE: false,

  // 画布name
  flowNmae: '',

  hasAllNodes: false,

  disabledPublish: true,

  disabledPublishMessage: '',
  // 编辑视频节点集合状态
  cropingVideNodeIds: [],
  flowInitialized: false,

  // 查询历史任务信息的节点id
  nodeHistiryInfoId: '',
  // 成本计算结果
  costCalcResult: {
    cost: 0,
    time: 0,
  },
  // 成本计算弹窗是否打开
  costCalcModalOpen: false,
  // 成本计算执行回调
  costCalcExecuteCallback: null,
  // 成本计算将要执行的节点 ID 列表
  costCalcWillExecuteNodeIds: [],
  // 正在编辑标题的节点 id
  editingNodeId: '',

  feedbackCallback: undefined,
});

const useFlowStore = create<TFlowState>()(immer(flowSlice));

export function flowMtstatTrack(eventId: string, params: Record<string, any>) {
  mtstatTrack(eventId, {
    room_id: useFlowStore.getState().roomId,
    ...params,
  });
}

export default useFlowStore;

export const getMcpTaskingMap = () => useFlowStore.getState().mcpTaskingMap;

export const getAllExecuteIdMap = () => useFlowStore.getState().allExecuteIdMap;

/**
 * 设置全部执行id集合
 * 全部执行 id 平铺就可以
 * @param nodeId
 */
export const setAllExecuteIdMap = (nodeId: string) => {
  useFlowStore.setState((state) => {
    state.allExecuteIdMap[nodeId] = true;
  });
};

// 设置roomId
export function setRoomId(data: string) {
  useFlowStore.setState(() => ({ roomId: data }));
}

/**
 * 设置反馈回调
 * @param cb 反馈回调函数
 */
export function setFeedbackCallback(cb?: FeedbackCallbackType) {
  useFlowStore.setState(() => ({ feedbackCallback: cb }));
}

/**
 * 设置mcp服务配置
 *
 * @export
 * @param {Record<string, McpType[]>} data
 */
export function setMcpConfigList(data: McpType[]) {
  useFlowStore.setState(() => ({ mcpConfigList: data }));
}

/**
 * 设置mcp服务分类
 *
 * @export
 * @param {Record<string, McpCategoriesType[]>} data
 */
export function setMcpCategoriesList(data: McpCategoriesType[]) {
  useFlowStore.setState(() => ({ mcpCategoriesList: data }));
}

/**
 * 设置映射 mcpNameMap
 * @param data
 */
export function setMcpNameMap(data: Record<string, string>) {
  useFlowStore.setState(() => ({ mcpNameMap: data }));
}

/**
 *  设置当前节点的禁用状态
 * @param {string} id
 * @param {INodeDisabledConfig} disabled
 */
export function setNodeDisabledConfig(id: string, nodeDisabledConfig: INodeDisabledConfig) {
  useFlowStore.setState((state) => {
    return {
      nodeDisabledConfig: {
        ...state.nodeDisabledConfig,
        [id]: nodeDisabledConfig,
      },
    };
  });
}
/*
 * 设置mcp服务任务中状态
 *
 * @export
 * @param {string} nodeId
 */
export function setMcpTaskingMap(nodeId: string, item: MCPTaskingItem) {
  useFlowStore.setState((state) => {
    state.mcpTaskingMap[nodeId] = item;
  });
}

/**
 * 删除mcp服务任务中状态
 *
 * @export
 * @param {string} nodeId
 */

export function removeMcpTaskingMap(nodeId: string) {
  useFlowStore.setState((state) => {
    Reflect.deleteProperty(state.mcpTaskingMap, nodeId);
  });
  useFlowStore.setState((state) => {
    Reflect.deleteProperty(state.allExecuteIdMap, nodeId);
  });
}

/**
 * 设置用户是否操作过节点
 *
 * @export
 * @param {boolean} hasOperation
 */
export function setHasNodeOperation(hasOperation: boolean) {
  useFlowStore.setState(() => ({ hasNodeOperation: hasOperation }));
}

/**
 * 单节点选中id
 *
 * @export
 * @param {string} id
 */
export function setHoverNodeId(id: string) {
  useFlowStore.setState(() => ({ hoverNodeId: id }));
}

/**
 * 设置正在编辑标题的节点 id
 *
 * @export
 * @param {string} id
 */
export function setEditingNodeId(id: string) {
  useFlowStore.setState(() => ({ editingNodeId: id }));
}
/** *
 *
 *  视频剪辑处理编辑nodeids
 *
 */
export function setCropingVideNodeIds(ids: string[]) {
  useFlowStore.setState(() => ({ cropingVideNodeIds: ids }));
}

/**
 * 设置是否正在hover
 *
 *
 * @export
 * @param {boolean} isHovered
 */
export function setIsHovered(isHovered: boolean) {
  useFlowStore.setState(() => ({ isHovered }));
}

/**
 * 设置正在上传的节点
 *
 * @export
 * @param {string} id
 */

export function setUploadingNodes(id: string) {
  useFlowStore.setState((state) => {
    return {
      uploadingNodes: [...state.uploadingNodes, id],
    };
  });
}

/**
 * 移除正在上传的节点
 *
 * @export
 * @param {string} id
 */

export function removeUploadingNodes(id: string) {
  useFlowStore.setState((state) => {
    return {
      uploadingNodes: state.uploadingNodes.filter((item) => item !== id),
    };
  });
}

/**
 * 设置正在执行的工作流列表
 *
 * @param executeWorkflowOptions
 */
export function setExecuteWorkflowOptions(executeWorkflowOptions: Array<IExecuteWorkflowItem>) {
  useFlowStore.setState(() => ({ executeWorkflowOptions }));
}

/**
 * 设置是否正在执行 SSE
 *
 * @param isExecutingSSE
 */
export function setIsExecutingSSE(isExecutingSSE: boolean) {
  useFlowStore.setState(() => ({ isExecutingSSE }));
}

/* 设置在分享中状态
 *
 * @export
 * @param {boolean} inShared
 */
export function setInShared(inShared: boolean) {
  useFlowStore.setState(() => ({ inShared }));
}

/**
 * 设置事件流房间名称
 *
 * @export
 * @param {string} flowNmae
 */
export function setFlowName(flowNmae: string) {
  useFlowStore.setState(() => ({ flowNmae }));
}

/**
 * 设置分享时素材列表
 *
 * @export
 * @param {NodeMaterialItem[]} material_list
 */
export function setMaterialList(material_list: NodeMaterialItem[]) {
  useFlowStore.setState(() => ({ material_list }));
}

/**
 * 画布是否有节点
 * 只在添加或删除节点时更新 hasAllNodes 避免频繁更新store导致卡顿
 * @export
 * @param {boolean} hasAllNodes
 */
export function setHasAllNodes(hasAllNodes: boolean, event?: WorkflowContentChangeEvent) {
  if (!event || event.type === WorkflowContentChangeType.ADD_NODE || event.type === WorkflowContentChangeType.DELETE_NODE) {
    useFlowStore.setState(() => ({ hasAllNodes }));
  }
}

/**
 * 设置工作流发布按钮的禁用状态
 *
 * @export
 * @param {FreeLayoutPluginContext} ctx
 * @param {WorkflowNodeEntity[]} allNodes
 */
export async function setDisabledPublish(ctx: FreeLayoutPluginContext, event?: WorkflowContentChangeEvent) {
  if (!event
    || event.type === WorkflowContentChangeType.ADD_NODE
    || event.type === WorkflowContentChangeType.DELETE_NODE
    || event.type === WorkflowContentChangeType.NODE_DATA_CHANGE
    || event.type === WorkflowContentChangeType.ADD_LINE
    || event.type === WorkflowContentChangeType.DELETE_LINE
  ) {
    const { disabledPublish, disabledPublishMessage } = await getPublishDisableStatus(ctx);
    useFlowStore.setState(() => ({ disabledPublish, disabledPublishMessage }));
  }
}

/**
 * 设置画布初始化状态
 *
 * @export
 * @param {boolean} initialized
 */
export function setFlowInitialized(initialized: boolean) {
  useFlowStore.setState(() => ({ flowInitialized: initialized }));
}

/**
 * 等待画布初始化完成
 *
 * @export
 * @returns {Promise<void>}
 */
export function waitForFlowInitialized(): Promise<void> {
  return new Promise((resolve) => {
    const state = useFlowStore.getState();
    if (state.flowInitialized) {
      resolve();
      return;
    }
    const unsubscribe = useFlowStore.subscribe((newState) => {
      if (newState.flowInitialized) {
        unsubscribe();
        resolve();
      }
    });
  });
}

/**
 * 设置节点详情的任务id
 *
 * @export
 * @param {string} taskId
 */
export function setNodeHistiryInfoId(id: string) {
  useFlowStore.setState(() => ({ nodeHistiryInfoId: id }));
}

/**
 * 设置成本计算结果
 * @param costCalcResult { cost: number; time: number; }
 */
export function setCostCalcResult(costCalcResult: { cost: number; time: number; }) {
  useFlowStore.setState(() => ({ costCalcResult }));
}

/**
 * 设置成本计算弹窗是否打开
 * @param costCalcModalOpen boolean
 */
export function setCostCalcModalOpen(costCalcModalOpen: boolean) {
  useFlowStore.setState(() => ({ costCalcModalOpen }));
}

/**
 * 设置成本计算执行回调
 * @param callback (() => void) | null
 */
export function setCostCalcExecuteCallback(callback: (() => void) | null) {
  useFlowStore.setState(() => ({ costCalcExecuteCallback: callback }));
}

/**
 * 设置成本计算将要执行的节点 ID 列表
 * @param ids string[]
 */
export function setCostCalcWillExecuteNodeIds(ids: string[]) {
  useFlowStore.setState(() => ({ costCalcWillExecuteNodeIds: ids }));
}
